import * as React from 'react';
import './pill.scss';
import { cn, formatDate, getEmployeeName } from '../../utils';
import { Metadata } from '../../types';
import { NodeType } from '../../enums/NodeType';
import { useSelector } from '../../utils/useSelector';

interface PillsProps
  extends Pick<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  commaSeparated?: boolean;
  shouldMask?: boolean;
  className?: string;
}

export const Pills = ({
  children,
  commaSeparated,
  shouldMask,
  className
}: PillsProps) => (
  <div
    className={cn(className, { 'pills-comma': commaSeparated }, 'pills', {
      'fs-mask': shouldMask
    })}
  >
    {children}
  </div>
);

type PillProps = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'children' | 'title'
> & {
  name: string;
  postfix?: string;
  data?: Partial<Metadata>;
  isSelected?: boolean;
  separator?: string | boolean | ((n: string) => string);
};

const getSeparator = (n: string, opt: any) => {
  switch (typeof opt) {
    case 'string':
      return defaultSeparator(n, opt);
    case 'function':
      return opt(n);
  }
  return defaultSeparator(n);
};

const pun = /[.,!?][ ]*$/;
const defaultSeparator = (n: string = '', defSep: string = ',') => {
  if (typeof n != 'string') n = '' + JSON.stringify(n);
  if (n.match(pun)?.length) {
    return ' ';
  }
  return defSep + ' ';
};

export const Pill = ({
  className,
  children,
  name,
  data,
  postfix = '',
  separator,
  isSelected,
  ...p
}: PillProps) => {
  const practiceCors = useSelector(s => s.planning?.practiceCors);
  const sep = getSeparator(name, separator);
  if (typeof name != 'string') name = JSON.stringify(name);
  const pillData =
    data?.labels?.includes(NodeType.Practice) &&
    !(data as any)?.cor?.props?.name &&
    data?.props?.refId
      ? {
          ...data,
          cor: practiceCors?.[data.props.refId]
        }
      : data;
  const names = getPillNames(name, pillData, isSelected);
  name = names.shift();
  const multiLine = names.length > 0;
  if (!multiLine) {
    return (
      <div className={cn('pill', className)} {...p}>
        <div
          className={cn({ 'pill-newline': sep.trim() == '\\n' })}
          data-separator={sep}
        >
          {name}
          {postfix}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className={cn('pill', className, 'pill-multi-line')} {...p}>
      <div>
        <div
          className={cn({ 'pill-newline': sep.trim() == '\\n' })}
          data-separator={sep}
        >
          {name}
          {postfix}
        </div>
        {names.map((n, i) => (
          <div key={i}>{n}</div>
        ))}
      </div>
      {children}
    </div>
  );
};

const getPillNames = (
  name: string,
  data: Partial<Metadata>,
  isSelected: boolean
): string[] => {
  const itemId = data?.props?.itemId;
  if (itemId) {
    return [`${itemId} - ${name ? name : '<Title not defined>'}`];
  }
  const labels = data?.labels || [];
  if (labels.includes(NodeType.Practice) && !isSelected) {
    const cor = (data as any)?.cor;
    const corName =
      cor?.props?.name ||
      [cor?.props?.empFstNm, cor?.props?.empLstNm].filter(x => !!x).join(' ');
    return [
      `${name} / ${
        corName && corName.trim().length > 0 ? corName : `<CoR not assigned>`
      }`
    ];
  }
  if (labels.includes(NodeType.Employee)) {
    return [
      getEmployeeName(data, {
        withEmpCd: true,
        withInactive: true,
        withAudit: true
      })
    ];
  }
  if (labels.includes(NodeType.Conference) && data?.props) {
    const { title, code, regionCode, location, startDate } = data.props;
    return [
      title + (code ? ` (${code})` : ''),
      [regionCode, location, formatDate(startDate)].filter(x => x).join(', ')
    ];
  }
  return [name];
};
